import { CARD_API_ENDPOINT } from '@/config/consts'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { type Address, formatUnits, parseUnits } from 'viem'
import { useAccount, useChainId, usePublicClient, useReadContracts, useSendTransaction } from 'wagmi'
import { waitForTxReceipt } from '@/utils/waitForTxReceipt'
import { delegationTokenAbi } from '../../../../abis/evm/delegationToken'
import { CREDIT_TYPES } from '../constants'
import { createSmartFetch } from './useAuthenticatedMutation'
import { useCreditReceiver } from './useCreditReceiver'

export interface DelegationParams {
  creditLimit: number
}

interface UseDelegationParams {
  requiredLimit?: number
  debtTokenAddress?: Address
  borrowTokenAddress?: Address
  creditType?: string
}

export function useDelegation({
  requiredLimit = 0,
  debtTokenAddress,
  borrowTokenAddress,
  creditType = CREDIT_TYPES.HYPURRFI_BORROW,
}: UseDelegationParams = {}) {
  const { address } = useAccount()
  const chainId = useChainId()
  const queryClient = useQueryClient()
  const publicClient = usePublicClient()

  const { sendTransactionAsync, isPending: isApproving, error: approveError, data: txHash } = useSendTransaction()

  const {
    creditReceiver: delegateeAddress,
    isLoading: isLoadingDelegatee,
  } = useCreditReceiver()
  const resolvedDelegateeAddress = delegateeAddress
  const isLoadingResolvedDelegatee = !resolvedDelegateeAddress && isLoadingDelegatee

  const {
    data: contractData,
    isLoading: isLoadingAllowance,
    isSuccess: isAllowanceLoaded,
    refetch: refetchAllowance,
  } = useReadContracts({
    contracts: [
      {
        address: debtTokenAddress,
        abi: delegationTokenAbi,
        functionName: 'decimals',
      },
      {
        address: debtTokenAddress,
        abi: delegationTokenAbi,
        functionName: 'borrowAllowance',
        args: address && resolvedDelegateeAddress ? [address, resolvedDelegateeAddress] : undefined,
      },
    ],
    query: {
      enabled: !!address && !!resolvedDelegateeAddress && !!debtTokenAddress,
      staleTime: 60_000 * 5,
      refetchOnWindowFocus: false,
    },
  })

  const tokenDecimals = (contractData?.[0]?.result as number) ?? 18
  const currentAllowance = contractData?.[1]?.result as bigint | undefined

  const hasRequiredAddresses = !!debtTokenAddress && !!resolvedDelegateeAddress

  const hasSufficientDelegation = useMemo(() => {
    if (!hasRequiredAddresses || !isAllowanceLoaded || currentAllowance == null) return false
    try {
      const requiredRaw = parseUnits(String(Math.ceil(requiredLimit)), tokenDecimals)
      return currentAllowance >= requiredRaw
    } catch {
      return false
    }
  }, [hasRequiredAddresses, isAllowanceLoaded, currentAllowance, requiredLimit, tokenDecimals])
  const needsDelegation = hasRequiredAddresses && isAllowanceLoaded && !hasSufficientDelegation && requiredLimit > 0

  const approveDelegation = useMutation({
    mutationFn: async (params: DelegationParams) => {
      if (!address) {
        throw new Error('Wallet not connected')
      }

      if (!borrowTokenAddress) {
        throw new Error('Borrow token address not available')
      }

      try {
        const delegateFetch = createSmartFetch(`${CARD_API_ENDPOINT}/v1/vendor/cards/users/credit/delegate`, {
          method: 'POST',
          body: JSON.stringify({
            creditType,
            creditLimit: params.creditLimit,
            chainID: chainId,
            userParams: {
              borrowTokenAddress,
            },
          }),
        })

        const apiData = await delegateFetch({})
        if (!apiData?.data?.to || !apiData?.data?.callData) {
          throw new Error('Invalid delegation response from server')
        }
        const { to, callData, value } = apiData.data

        // Send the transaction from backend (uses correct delegatee address)
        const hash = await sendTransactionAsync({
          to,
          value: BigInt(value || 0),
          data: callData,
        })

        if (publicClient) {
          await waitForTxReceipt(publicClient, hash)
        }

        queryClient.invalidateQueries({
          queryKey: ['readContract'],
        })

        // Refetch allowance immediately
        await refetchAllowance()

        return hash
      } catch (error) {
        // Enhance error messages for user rejections
        const errorMessage = error instanceof Error ? error.message : String(error)
        if (
          errorMessage.includes('rejected') ||
          errorMessage.includes('denied') ||
          errorMessage.includes('cancelled')
        ) {
          throw new Error('User rejected the delegation approval request')
        }
        throw error
      }
    },
  })

  const resetError = () => {
    approveDelegation.reset()
  }

  return {
    // Allowance info
    currentAllowance: currentAllowance ? Number(formatUnits(currentAllowance, tokenDecimals)) : 0,
    requiredLimit, // Required limit in token units

    // Delegation status
    needsDelegation,
    hasSufficientDelegation,

    // Loading states — only true when a query is actively in flight
    isLoadingAllowance: isLoadingResolvedDelegatee || (hasRequiredAddresses && isLoadingAllowance),
    isAllowanceLoaded: hasRequiredAddresses && isAllowanceLoaded && !isLoadingResolvedDelegatee,

    // Actions
    approveDelegation: approveDelegation.mutateAsync,
    refetchAllowance,

    // Transaction states
    isApproving,
    isConfirming: !isApproving && approveDelegation.isPending,
    isConfirmed: approveDelegation.isSuccess,
    txHash,
    approveError: approveError || approveDelegation.error,
    resetError,

    // Config
    delegationTarget: resolvedDelegateeAddress,
  }
}
